import React, { useContext, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Avatar, Divider, Dropdown, MenuProps, Space } from 'antd'
import { ProvidenceLogo } from '../../assets'
import { UserContext } from '../../core/user-context/user-context-middleware'
import './sideMenu.scss'
import {
  Pages,
  Roles,
  notificationData,
  RoleBasedPageMapping,
  decryptIdToken,
} from '../../pages/constants'
import { homePath } from '../../routing'
import { SignOutButton } from '../authentication/signout-button/signout-button'
import SideMenu from './sideMenu'
import {
  adminPath,
  peerSupporterLeadPathStub,
  supporterPathStub,
} from '../../routing/route.constants'
import { MetadataContext } from '../../pages/home/middleware/metadata-middleware'
import { ProfilePage } from './profile-page'
import { API_URLs } from '../../configuration/api-urls'
import { getArticle, getLastUpdatedAt } from '../../common/utils/utils'
import { globalConfig } from '../../configuration/config'
import { useAPICallProps } from '../../common/api-utils/use-api-call'
import { MenuOutlined } from '@ant-design/icons'

interface IHeaderNavLinks {
  title: string
  page: Pages
  onClick: () => void
  roles: Roles[]
}

export type RoleType = {
  Id: string
  Name: string
}

export type Location = {
  Id: string
  Name: string
}

export interface Notificationinterface {
  Id: string
  RoleType: RoleType
  Location: Location
  createdAt: string
}

export type LogoutFunction = () => void
export interface AppHeaderProps {
  logOut?: LogoutFunction
}

export interface IRecentNotifications {
  id: string
  roleType: {
    id: string
    name: string
  }
  location: {
    id: string
    name: string
  }
  createdAt: string
}

export interface INotificationResponse extends useAPICallProps {
  notificationCount: string
  requestNotificationDataDetails: IRecentNotifications[]
}

const AppHeader: React.FC<AppHeaderProps> = ({}) => {
  const userDetails = useContext(UserContext)
  const { userDisplayName, userPhoto, userInitials, userEmail } = userDetails
  const { userRole } = useContext(MetadataContext)
  const history = useHistory()
  const location = useLocation()
  const [currentPage, setCurrentPage] = useState<Pages>(Pages.HOMEPAGE)
  const [currentNotificationCount, setCurrentNotificationCount] =
    useState<string>('0')
  const [currentNotifications, setCurrentNotifications] = useState<
    IRecentNotifications[]
  >([])
  const [isSideMenuVisible, setSideMenuVisible] = useState(false)
  const [idToken, setIdToken] = useState<string | null>(null)

  const notificationFetchUrl = globalConfig
    .get()
    .apiBaseUrl.concat(API_URLs.v1.GET_PeerSupporterNotifications)

  const UserHeaderNavLinks: IHeaderNavLinks[] = [
    {
      title: 'Home',
      page: Pages.HOMEPAGE,
      onClick: () => history.push(homePath),
      roles: [
        Roles.SUPPORTER,
        Roles.PEER_SUPPORTER_LEAD,
        Roles.ADMIN,
        Roles.ADMIN_SUPPORTER,
        Roles.ADMIN_LEAD,
        Roles.LEAD_SUPPORTER,
        Roles.ALL,
      ],
    },
    {
      title: 'Supporter',
      page: Pages.SUPPORTPAGE,
      onClick: () =>
        history.push(supporterPathStub.concat('/awaiting-referrals')),
      roles: [
        Roles.SUPPORTER,
        Roles.ADMIN_SUPPORTER,
        Roles.LEAD_SUPPORTER,
        Roles.ALL,
      ],
    },
    {
      title: 'Peer Supporter Lead',
      page: Pages.PEERSUPPORTERLEAD,
      onClick: () =>
        history.push(peerSupporterLeadPathStub.concat('/supporter-management')),
      roles: [
        Roles.PEER_SUPPORTER_LEAD,
        Roles.LEAD_SUPPORTER,
        Roles.ADMIN_LEAD,
        Roles.ADMIN,
        Roles.ADMIN_SUPPORTER,
        Roles.ALL,
      ],
    },
    {
      title: 'Admin',
      page: Pages.ADMINPAGE,
      onClick: () => history.push(adminPath),
      roles: [Roles.ADMIN, Roles.ADMIN_LEAD, Roles.ADMIN_SUPPORTER, Roles.ALL],
    },
  ]

  useEffect(() => {
    const fetchDecryptedToken = async () => {
      const decryptedToken = await decryptIdToken()
      setIdToken(decryptedToken ?? idToken)
    }

    fetchDecryptedToken()
  }, [])

  useEffect(() => {
    if (location.pathname === homePath) {
      setCurrentPage(Pages.HOMEPAGE)
    }
    if (location.pathname.includes('/supporter')) {
      setCurrentPage(Pages.SUPPORTPAGE)
    }
    if (location.pathname.includes('/peersupporterlead')) {
      setCurrentPage(Pages.PEERSUPPORTERLEAD)
    }
    if (location.pathname.includes('/admin')) {
      setCurrentPage(Pages.ADMINPAGE)
    }
  }, [setCurrentPage, location.pathname])

  useEffect(() => {
    const encryptedToken = sessionStorage.getItem('encryptedToken') || ''
    const encryptedAESKey = sessionStorage.getItem('encryptedAESKey') || ''
    if (idToken && RoleBasedPageMapping.notifications.includes(userRole)) {
      fetch(notificationFetchUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          EncryptedAESKey: encryptedAESKey,
          EncryptedToken: encryptedToken,
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.json()
        })
        .then((data: INotificationResponse) => {
          setCurrentNotifications(data.requestNotificationDataDetails)
          setCurrentNotificationCount(data.notificationCount)
        })
        .catch((error) => {
          console.log('Unable to fetch notifications - ', error)
        })
    }
  }, [idToken, location, userRole])

  const profileItems: MenuProps['items'] = []

  if (RoleBasedPageMapping.profile.includes(userRole)) {
    profileItems.push({
      label: <ProfilePage />,
      key: '0',
    })
  }
  profileItems.push({
    label: <SignOutButton />,
    key: '1',
  })

  const notificationItems: MenuProps['items'] =
    currentNotifications && currentNotifications.length > 0
      ? currentNotifications.map((item, index) => {
          return {
            label: (
              <div className="w-full flex flex-col gap-2">
                <div className="font-normal text-sm text-black">
                  {getArticle(item.roleType.name)} {item.roleType.name} from{' '}
                  {item.location.name} raised a P2P support request
                </div>
                <div className="font-light text-xs">
                  {getLastUpdatedAt(item.createdAt)}
                </div>
                {index < notificationData.length - 1 && (
                  <Divider style={{ margin: 0 }} />
                )}
              </div>
            ),
            key: index.toString(),
          }
        })
      : [
          {
            label: (
              <div className="w-full flex flex-col gap-2">
                <div className="font-normal text-sm text-black">
                  No new notifications at this time.
                </div>
              </div>
            ),
            key: 0,
          },
        ]

  const toggleSideMenu = () => {
    setSideMenuVisible(!isSideMenuVisible)
  }

  return (
    <div className="flex flex-row fixed w-full top-0 z-50 justify-between items-center h-20 px-4 md:px-14 2xl:pr-20 2xl:pl-[5%] bg-white">
      <a href="/">
        <img
          src={ProvidenceLogo}
          alt="Providence Logo"
          className="max-w-[10rem] md:max-w-[12rem] cursor-pointer 2xl:ml-14"
        />
      </a>
      <div className="flex flex-row justify-center items-center gap-4">
        <div className="flex flex-row items-center justify-center gap-4 md:pr-2 lg:pr-4 xl:pr-8 font-barlow">
          {UserHeaderNavLinks.map((userNavLink, index) => {
            if (userNavLink.roles.some((role) => role === userRole))
              return (
                <button
                  className={`hidden lg:block text-[0.9rem] cursor-pointer hover:font-medium border-none bg-transparent ${
                    currentPage === userNavLink.page
                      ? 'font-medium'
                      : 'font-normal'
                  }`}
                  onClick={userNavLink.onClick}
                  key={index}
                >
                  {userNavLink.title}
                </button>
              )
          })}
          {RoleBasedPageMapping.notifications.includes(userRole) ? (
            <button className="hidden lg:block text-sm font-normal cursor-pointer hover:font-medium border-none bg-transparent">
              <Dropdown
                menu={{ items: notificationItems }}
                arrow={true}
                overlayClassName="h-[32rem] w-80 Notification-Dropdown md:fixed"
                dropdownRender={(menu) => (
                  <div className="h-96 w-80 drop-shadow-md flex flex-col font-barlow overflow-y-auto">
                    <div className="h-12 bg-[#ECECEC] w-full text-[0.9rem] font-medium p-2">
                      Notifications
                    </div>
                    <div>{React.cloneElement(menu as React.ReactElement)}</div>
                  </div>
                )}
              >
                <div className="flex flex-row items-center justify-center gap-1">
                  Notifications
                  {currentNotificationCount ? (
                    <div className="bg-component-red drop-shadow-md rounded w-4 text-white flex justify-center text-xs">
                      {currentNotificationCount}
                    </div>
                  ) : null}
                </div>
              </Dropdown>
            </button>
          ) : null}
        </div>
        <Dropdown
          menu={{ items: profileItems }}
          arrow={true}
          className="cursor-pointer hidden lg:flex"
        >
          <Space>
            <div>
              {userPhoto ? (
                <Avatar
                  size={'large'}
                  src={userPhoto}
                  className="hidden md:flex"
                  alt="Your profile picture"
                />
              ) : (
                <Avatar size={'large'} className="bg-white-600 hidden md:flex">
                  {userInitials}
                </Avatar>
              )}
            </div>
            <div className="flex flex-col items-start justify-center">
              <div className="text-sm font-medium max-w-xs text-ellipsis">
                {userDisplayName?.split(',').reverse().join(' ')}
              </div>
              <div className="text-[10px] font-light max-w-xs text-ellipsis">
                {userEmail}
              </div>
            </div>
          </Space>
        </Dropdown>
      </div>
      <SideMenu
        visible={isSideMenuVisible}
        onClose={toggleSideMenu}
        userRole={userRole}
        currentNotifications={currentNotifications}
      />
      <MenuOutlined
        className="pt-2 ml-3 relative text-2xl lg:hidden"
        alt="MenuIcon"
        onClick={toggleSideMenu}
      />
    </div>
  )
}

export default AppHeader
